import { useEffect, useRef } from 'react'

/**
 * A modal built on the browser's own `<dialog>`, because the browser already does the hard parts.
 *
 * Focus trapping, restoring focus to whatever opened it, Escape, making the rest of the page inert
 * and painting a backdrop are all behaviors a hand-rolled overlay has to reimplement and usually
 * gets wrong on a screen reader. `showModal()` gives all of them, which is the difference between
 * this and the popover in `AccountMenu.tsx` - that one is a menu and deliberately does not trap.
 *
 * This is not the "browser dialog" that `AccountListPage` and `TenantListPage` argued against. Their
 * objection is to `window.prompt`: a box that cannot be corrected, cannot be re-read, and blocks the
 * page it belongs to. This is a form on the page, in the page's own type and colors, which can hold
 * as many fields as it needs and can show a server's refusal next to the field that caused it.
 *
 * Nothing is rendered until it is open. A dialog whose children mount with the page would run their
 * effects and hold their state for a form nobody has asked for yet, and every caller here resets its
 * fields on open - which is a thing to do on mount rather than a thing to remember in an effect.
 */
export interface DialogProps {
  open: boolean
  title: string
  onClose: () => void
  children: React.ReactNode
  /** Widens the sheet for a form with two columns of fields. */
  wide?: boolean
  'data-testid'?: string
}

export function Dialog({ open, title, onClose, children, wide, ...rest }: DialogProps) {
  const reference = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const element = reference.current
    if (!element) return

    // Guarded because `open` can be true across a re-render that has nothing to do with opening, and
    // calling showModal() on an already-modal dialog throws rather than being ignored.
    if (open && !element.open) element.showModal()
    else if (!open && element.open) element.close()
  }, [open])

  if (!open) return null

  return (
    <dialog
      ref={reference}
      className={wide ? 'dialog wide' : 'dialog'}
      aria-label={title}
      data-testid={rest['data-testid']}
      // Escape fires `cancel` rather than `close`, and the default would close the element without
      // telling React - leaving `open` true and the dialog shut until something else re-rendered it.
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      // The backdrop is part of the dialog element rather than a child, so a click on it reports the
      // dialog itself as the target. A click on anything inside reports that child.
      onClick={(event) => {
        if (event.target === reference.current) onClose()
      }}
    >
      <div className="dialog-head">
        <h3 className="dialog-title">{title}</h3>
        <button type="button" className="dialog-close" aria-label="Close" onClick={onClose} data-testid="dialog-close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="dialog-body">{children}</div>
    </dialog>
  )
}
